import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Sparkles,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Loader2,
  MapPin,
  Globe,
  Building2,
  Lock,
  Unlock,
  RotateCcw,
  Ban,
  Eye,
  ChevronDown,
  ChevronUp,
  Receipt,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: FileText, label: "Registrations", path: "/admin/registrations" },
  { icon: AlertTriangle, label: "OCR Exceptions", path: "/admin/ocr-exceptions" },
  { icon: Store, label: "Retailers", path: "/admin/retailers" },
  { icon: Gift, label: "Rewards", path: "/admin/rewards" },
  { icon: Sparkles, label: "Entitlements", path: "/admin/entitlements-v2" },
  { icon: Receipt, label: "Receipts", path: "/admin/receipts" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Monitor, label: "Screens", path: "/admin/screens" },
  { icon: ListChecks, label: "Playlists", path: "/admin/playlists" },
  { icon: Megaphone, label: "Advertisers", path: "/admin/advertisers" },
  { icon: TrendingUp, label: "Campaigns", path: "/admin/campaigns" },
  { icon: CreditCard, label: "Billing", path: "/admin/billing" },
  { icon: TrendingUp, label: "Revenue", path: "/admin/revenue" },
  { icon: Wallet, label: "Commercial", path: "/admin/commercial", section: "commercial" },
  { icon: DollarSign, label: "Finance Centre", path: "/admin/commercial/finance", section: "commercial" },
  { icon: Package, label: "Products & Tariffs", path: "/admin/commercial/products-tariffs", section: "commercial" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

export default function AdminEntitlementsV2() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Dialog states
  const [revokeDialog, setRevokeDialog] = useState({ open: false, entitlement: null });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, entitlement: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, entitlement: null });
  const [revokeReason, setRevokeReason] = useState("");
  const [restoreReason, setRestoreReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (siteFilter !== "all") params.append("site_id", siteFilter);
      params.append("limit", "100");
      
      const [entitlementsRes, statsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/entitlements-v2?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/entitlements-v2/stats/summary`, { withCredentials: true }),
        axios.get(`${API}/sites`, { withCredentials: true })
      ]);
      
      setEntitlements(entitlementsRes.data?.entitlements || []);
      setStats(statsRes.data);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired, please login again");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, siteFilter, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  const handleRevoke = async () => {
    if (!revokeReason.trim()) {
      toast.error("Please provide a reason for revoking");
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/entitlements-v2/${revokeDialog.entitlement.entitlement_id}/revoke`,
        { reason: revokeReason },
        { withCredentials: true }
      );
      toast.success("Entitlement revoked successfully");
      setRevokeDialog({ open: false, entitlement: null });
      setRevokeReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke entitlement");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/entitlements-v2/${restoreDialog.entitlement.entitlement_id}/restore`,
        { reason: restoreReason },
        { withCredentials: true }
      );
      toast.success("Entitlement restored successfully");
      setRestoreDialog({ open: false, entitlement: null });
      setRestoreReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore entitlement");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle, label: "Available" },
      reserved: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Lock, label: "Reserved" },
      consumed: { bg: "bg-blue-100", text: "text-blue-700", icon: CheckCircle, label: "Consumed" },
      expired: { bg: "bg-slate-100", text: "text-slate-600", icon: Clock, label: "Expired" },
      revoked: { bg: "bg-red-100", text: "text-red-700", icon: Ban, label: "Revoked" }
    };
    
    const badge = badges[status] || badges.expired;
    const Icon = badge.icon;
    
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getScopeBadge = (scope) => {
    if (scope === "network") {
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
          <Globe className="w-3 h-3" />
          Network-Wide
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        <Building2 className="w-3 h-3" />
        Site Only
      </span>
    );
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site?.name || siteId;
  };

  const filteredEntitlements = entitlements.filter(ent => {
    const matchesSearch = !searchTerm || 
      ent.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ent.qualifying_retailer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ent.entitlement_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesScope = scopeFilter === "all" || ent.scope === scopeFilter;
    
    return matchesSearch && matchesScope;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-entitlements-v2">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' }}
            >
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">ParkWise</h1>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === "/admin/entitlements-v2";
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-purple-500/20 text-purple-300"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-purple-400" />
                Entitlements Engine v2
              </h1>
              <p className="text-slate-400 mt-1">
                Full state machine: available, reserved, consumed, expired, revoked
              </p>
            </div>
            <Button 
              onClick={fetchData} 
              variant="outline" 
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.available || 0}</p>
                      <p className="text-xs text-slate-400">Available</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.reserved || 0}</p>
                      <p className="text-xs text-slate-400">Reserved</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.consumed || 0}</p>
                      <p className="text-xs text-slate-400">Consumed Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <Ban className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.revoked || 0}</p>
                      <p className="text-xs text-slate-400">Revoked Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.all_time?.consumed || 0}</p>
                      <p className="text-xs text-slate-400">All-Time Used</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search by vehicle, retailer, or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="search-input"
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700 text-white" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Status</SelectItem>
                    <SelectItem value="available" className="text-white">Available</SelectItem>
                    <SelectItem value="reserved" className="text-white">Reserved</SelectItem>
                    <SelectItem value="consumed" className="text-white">Consumed</SelectItem>
                    <SelectItem value="expired" className="text-white">Expired</SelectItem>
                    <SelectItem value="revoked" className="text-white">Revoked</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={scopeFilter} onValueChange={setScopeFilter}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white" data-testid="scope-filter">
                    <SelectValue placeholder="Scope" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Scopes</SelectItem>
                    <SelectItem value="site_only" className="text-white">Site Only</SelectItem>
                    <SelectItem value="network" className="text-white">Network-Wide</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={siteFilter} onValueChange={setSiteFilter}>
                  <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700 text-white" data-testid="site-filter">
                    <MapPin className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Site" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Sites</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Entitlements Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Entitlements ({filteredEntitlements.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                </div>
              ) : filteredEntitlements.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No entitlements found</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Try adjusting your filters
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Vehicle
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Scope
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Source
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Value
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredEntitlements.map((ent) => (
                        <>
                          <tr 
                            key={ent.entitlement_id} 
                            className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                            onClick={() => setExpandedRow(expandedRow === ent.entitlement_id ? null : ent.entitlement_id)}
                            data-testid={`entitlement-row-${ent.entitlement_id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {expandedRow === ent.entitlement_id ? 
                                  <ChevronUp className="w-4 h-4 text-slate-500" /> : 
                                  <ChevronDown className="w-4 h-4 text-slate-500" />
                                }
                                <span className="font-mono font-semibold text-white">
                                  {ent.vehicle_registration}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {getScopeBadge(ent.scope)}
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-white font-medium capitalize">{ent.source || "ayr_miles"}</p>
                                <p className="text-xs text-slate-500">{getSiteName(ent.site_id)}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-green-400 font-semibold">
                                {ent.free_hours}hr free
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-300">
                              {formatDateTime(ent.created_at)}
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(ent.status)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-slate-400 hover:text-white"
                                  onClick={() => setDetailDialog({ open: true, entitlement: ent })}
                                  data-testid={`view-btn-${ent.entitlement_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                
                                {ent.status === "available" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300"
                                    onClick={() => setRevokeDialog({ open: true, entitlement: ent })}
                                    data-testid={`revoke-btn-${ent.entitlement_id}`}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                                
                                {ent.status === "revoked" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-400 hover:text-green-300"
                                    onClick={() => setRestoreDialog({ open: true, entitlement: ent })}
                                    data-testid={`restore-btn-${ent.entitlement_id}`}
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          
                          {/* Expanded Row Details */}
                          {expandedRow === ent.entitlement_id && (
                            <tr className="bg-slate-800/20">
                              <td colSpan={7} className="px-4 py-4">
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <p className="text-slate-500">Entitlement ID</p>
                                    <p className="text-white font-mono">{ent.entitlement_id}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Valid Until</p>
                                    <p className="text-white">{formatDateTime(ent.valid_until)}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Qualifying Retailer</p>
                                    <p className="text-white">{ent.qualifying_retailer_name || "—"}</p>
                                  </div>
                                  {ent.status === "reserved" && (
                                    <>
                                      <div>
                                        <p className="text-slate-500">Reserved Until</p>
                                        <p className="text-yellow-400">{formatDateTime(ent.reserved_until)}</p>
                                      </div>
                                      <div>
                                        <p className="text-slate-500">Session ID</p>
                                        <p className="text-white font-mono">{ent.reserved_by_session_id || "—"}</p>
                                      </div>
                                    </>
                                  )}
                                  {ent.status === "consumed" && (
                                    <>
                                      <div>
                                        <p className="text-slate-500">Consumed At</p>
                                        <p className="text-blue-400">{formatDateTime(ent.consumed_at)}</p>
                                      </div>
                                      <div>
                                        <p className="text-slate-500">Session ID</p>
                                        <p className="text-white font-mono">{ent.consumed_by_session_id || "—"}</p>
                                      </div>
                                    </>
                                  )}
                                  {ent.status === "revoked" && (
                                    <>
                                      <div>
                                        <p className="text-slate-500">Revoked At</p>
                                        <p className="text-red-400">{formatDateTime(ent.revoked_at)}</p>
                                      </div>
                                      <div>
                                        <p className="text-slate-500">Revoked Reason</p>
                                        <p className="text-white">{ent.revoked_reason || "—"}</p>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialog.open} onOpenChange={(open) => !open && setRevokeDialog({ open: false, entitlement: null })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" />
              Revoke Entitlement
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-400 mb-4">
              You are about to revoke the entitlement for vehicle{" "}
              <span className="font-mono font-bold text-white">{revokeDialog.entitlement?.vehicle_registration}</span>.
            </p>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Reason for revocation *</label>
              <Textarea
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Enter reason..."
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="revoke-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRevokeDialog({ open: false, entitlement: null })}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRevoke} 
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-revoke-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revoke Entitlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ open: false, entitlement: null })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-400" />
              Restore Entitlement
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-400 mb-4">
              Restore the entitlement for vehicle{" "}
              <span className="font-mono font-bold text-white">{restoreDialog.entitlement?.vehicle_registration}</span>?
            </p>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Reason (optional)</label>
              <Textarea
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                placeholder="Enter reason..."
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="restore-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRestoreDialog({ open: false, entitlement: null })}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRestore} 
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-restore-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Restore Entitlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, entitlement: null })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Entitlement Details
            </DialogTitle>
          </DialogHeader>
          {detailDialog.entitlement && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Entitlement ID</p>
                  <p className="text-white font-mono">{detailDialog.entitlement.entitlement_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Vehicle</p>
                  <p className="text-white font-mono font-bold">{detailDialog.entitlement.vehicle_registration}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Status</p>
                  {getStatusBadge(detailDialog.entitlement.status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Scope</p>
                  {getScopeBadge(detailDialog.entitlement.scope)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Source</p>
                  <p className="text-white capitalize">{detailDialog.entitlement.source || "ayr_miles"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Free Hours</p>
                  <p className="text-green-400 font-bold">{detailDialog.entitlement.free_hours}hr</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Site</p>
                  <p className="text-white">{getSiteName(detailDialog.entitlement.site_id)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Created</p>
                  <p className="text-white">{formatDateTime(detailDialog.entitlement.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Valid Until</p>
                  <p className="text-white">{formatDateTime(detailDialog.entitlement.valid_until)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Qualifying Retailer</p>
                  <p className="text-white">{detailDialog.entitlement.qualifying_retailer_name || "—"}</p>
                </div>
              </div>
              
              {/* Audit History */}
              {detailDialog.entitlement.audit_history?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs text-slate-500 uppercase mb-3">Audit History</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {detailDialog.entitlement.audit_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm bg-slate-800/50 rounded p-2">
                        <span className="text-slate-500">{formatDateTime(entry.timestamp)}</span>
                        <span className="text-white capitalize">{entry.action}</span>
                        <span className="text-slate-400">by {entry.by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
